import React, { useMemo } from 'react'
import { Text, TextProps as TextPropsOriginal } from 'rebass'
import styled, {
  createGlobalStyle,
  css,
  DefaultTheme,
  ThemeProvider as StyledComponentsThemeProvider,
} from 'styled-components/macro'

import { useIsDarkMode } from '../state/user/hooks'
import { Colors } from './styled'

export * from './components'

type TextProps = Omit<TextPropsOriginal, 'css'>

export const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
}

// Migrating to a standard z-index system https://getbootstrap.com/docs/5.0/layout/z-index/
// Please avoid using deprecated numbers
export enum Z_INDEX {
  deprecated_zero = 0,
  deprecated_content = 1,
  dropdown = 1000,
  sticky = 1020,
  fixed = 1030,
  modalBackdrop = 1040,
  offcanvas = 1050,
  modal = 1060,
  popover = 1070,
  tooltip = 1080,
}

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce(
  (accumulator, size) => {
    ;(accumulator as any)[size] = (a: any, b: any, c: any) => css`
      @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
        ${css(a, b, c)}
      }
    `
    return accumulator
  },
  {}
) as any

const white = '#FFFFFF'
const black = '#000000'

function colors(darkMode: boolean): Colors {
  return {
    darkMode,
    // base
    white,
    black,

    // text — UZH Grey scale
    text1: darkMode ? '#FFFFFF' : '#000000',
    text2: darkMode ? '#C2C2C2' : '#4D4D4D',
    text3: darkMode ? '#A3A3A3' : '#666666',
    text4: darkMode ? '#4D4D4D' : '#A3A3A3',
    text5: darkMode ? '#333333' : '#E7E7E7',

    // backgrounds — UZH navy / light grey
    bg0: darkMode ? '#050B24' : '#FFFFFF',
    bg1: darkMode ? '#0B1440' : '#FFFFFF',
    bg2: darkMode ? '#141F5C' : '#FAFAFA',
    bg3: darkMode ? '#22307E' : '#E0E0E0',
    bg4: darkMode ? '#4D4D4D' : '#C2C2C2',
    bg5: darkMode ? '#666666' : '#A3A3A3',
    bg6: darkMode ? '#001452' : '#0028A5',

    // specialty
    modalBG: darkMode ? 'rgba(5,11,36,0.6)' : 'rgba(0,40,165,0.15)',
    advancedBG: darkMode ? 'rgba(0,20,82,0.25)' : 'rgba(186,203,255,0.35)',

    // primary — UZH Blue (was Uniswap pink)
    primary1: darkMode ? '#3062FF' : '#0028A5',
    primary2: darkMode ? '#7596FF' : '#3062FF',
    primary3: darkMode ? '#7596FF' : '#7596FF',
    primary4: darkMode ? '#22307E' : '#BACBFF',
    primary5: darkMode ? '#141F5C' : '#DBF4F9',

    primaryText1: darkMode ? '#7596FF' : '#0028A5',

    // secondary — UZH Cyan
    secondary1: darkMode ? '#4AC9E3' : '#1EA7C4',
    secondary2: darkMode ? '#14708226' : '#DBF4F926',
    secondary3: darkMode ? '#14708226' : '#B7E9F426',

    // semantic — UZH Berry / Apple / Gold
    red1: darkMode ? '#F3537F' : '#BF0D3E',
    red2: darkMode ? '#F78CAA' : '#8F0A2E',
    red3: '#60061F',
    green1: darkMode ? '#C8E485' : '#7CA023',
    yellow1: darkMode ? '#FFDE8F' : '#FFC845',
    yellow2: darkMode ? '#F3AB00' : '#A27200',
    yellow3: darkMode ? '#FFC845' : '#FFC845',
    blue1: darkMode ? '#7596FF' : '#0028A5',
    blue2: darkMode ? '#92DFEE' : '#1EA7C4',

    blue4: darkMode ? '#141F5C' : '#BACBFF',
    error: darkMode ? '#F3537F' : '#BF0D3E',
    success: darkMode ? '#C8E485' : '#7CA023',
    warning: darkMode ? '#F3AB00' : '#A27200',
  }
}

function theme(darkMode: boolean): DefaultTheme {
  return {
    ...colors(darkMode),

    grids: {
      sm: 8,
      md: 12,
      lg: 24,
    },

    //shadows
    shadow1: darkMode ? '#000' : '#0028A5',

    // media queries
    mediaWidth: mediaWidthTemplates,

    // css snippets
    flexColumnNoWrap: css`
      display: flex;
      flex-flow: column nowrap;
    `,
    flexRowNoWrap: css`
      display: flex;
      flex-flow: row nowrap;
    `,
  }
}

export default function ThemeProvider({ children }: { children: React.ReactNode }) {
  const darkMode = useIsDarkMode()

  const themeObject = useMemo(() => theme(darkMode), [darkMode])

  return <StyledComponentsThemeProvider theme={themeObject}>{children}</StyledComponentsThemeProvider>
}

const TextWrapper = styled(Text)<{ color: keyof Colors }>`
  color: ${({ color, theme }) => (theme as any)[color]};
`

export const TYPE = {
  main(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text2'} {...props} />
  },
  link(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'primary1'} {...props} />
  },
  label(props: TextProps) {
    return <TextWrapper fontWeight={600} color={'text1'} {...props} />
  },
  black(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text1'} {...props} />
  },
  white(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'white'} {...props} />
  },
  body(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={16} color={'text1'} {...props} />
  },
  largeHeader(props: TextProps) {
    return <TextWrapper fontWeight={600} fontSize={24} {...props} />
  },
  mediumHeader(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={20} {...props} />
  },
  subHeader(props: TextProps) {
    return <TextWrapper fontWeight={400} fontSize={14} {...props} />
  },
  small(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={11} {...props} />
  },
  blue(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'blue1'} {...props} />
  },
  yellow(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'yellow3'} {...props} />
  },
  darkGray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'text3'} {...props} />
  },
  gray(props: TextProps) {
    return <TextWrapper fontWeight={500} color={'bg3'} {...props} />
  },
  italic(props: TextProps) {
    return <TextWrapper fontWeight={500} fontSize={12} fontStyle={'italic'} color={'text2'} {...props} />
  },
  error({ error, ...props }: { error: boolean } & TextProps) {
    return <TextWrapper fontWeight={500} color={error ? 'red1' : 'text2'} {...props} />
  },
}

export const ThemedGlobalStyle = createGlobalStyle`
html {
  color: ${({ theme }) => theme.text1};
  background-color: ${({ theme }) => theme.bg1} !important;
}

a {
 color: ${({ theme }) => theme.blue1}; 
}
`
